import { TabType } from '@/components/Header';
import Hepler from '@/components/Helper';
import Layout from '@/components/Layout';
import WhatsAppChat from '@/components/WhatsAppChat';
import BookingByCustomer from '@/containers/BookingByCustomer';
import TourBookingByCustomer from '@/containers/TourBookingByCustomer';
import { TourBookingProvider } from '@/containers/TourBookingByCustomer/providers/TourBookingProvider';
import TourBookingByCustomerHome from '@/containers/TourBookingByCustomerHome';
import TourBookingDetail from '@/containers/TourBookingDetail';
import TourBookingInfoDetail from '@/containers/TourBookingByCustomerHome/TourBookingInfoDetail';
import { Tour, TourType } from '@/types/tourBooking';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { ReactElement, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { HoiAnGoReviewsSection } from '@/components/Footer';
import { convertImages } from '@/utils/utils';
import HomePage from '@/components/HomePage';
import {
  getArticles,
  getEtaxi,
  getHomeContent,
  getMetaDataPage,
  PageMetaOutput,
} from '@/services/directus';
import { tourService } from '@/services/tourService';
import Head from 'next/head';
import useStoreApp from '@/store';

const Home = (
  _props: InferGetServerSidePropsType<typeof getServerSideProps>,
) => {

  const tabType = useMemo(() => {
    if (typeof _props.query.tab === 'string') {
      return _props.query.tab;
    }
    return undefined;
  }, [_props]);

  const router = useRouter();
  const { tour_id } = router.query as { tour_id: string | null };
  const user = useStoreApp(state => state.globalSlice.user);

  useEffect(() => {
    if (!tour_id && localStorage.getItem('order_confirm')) {
      localStorage.removeItem('order_confirm');
    }
  }, [tour_id]);

  if (tabType === TabType.ETAXI) {
    return <BookingByCustomer />;
  }

  if (tabType === TabType.TOUR) {
    return (
      <>
        <TourBookingProvider locale={_props.locale} tours={_props.tours}>
          {
            tour_id ?
              <TourBookingInfoDetail tours={_props.tours} locale={_props.locale ?? 'en'} /> :
              <TourBookingByCustomerHome tours={_props.tours} />
          }
        </TourBookingProvider>
      </>
    );
  }
  return (
    <>
      <Head>
        <title>{_props.metadata.title}</title>
        <meta name="description" content={_props.metadata.description} />
      </Head>
      <HomePage
        content={_props.homeContent}
        tours={_props.tours}
        overlayHeader={_props?.query?.from !== 'hoiango.com'}
      />
      {/*{process.env.NODE_ENV === 'production' && !user ? (*/}
      <HoiAnGoReviewsSection />
      {/*) : null}*/}
    </>
  );
};

Home.getLayout = (page: ReactElement, _props?: any) => (
  // HomePage branch: tab is neither ETAXI nor TOUR.
  <Layout
    hasBackground={false} // {_props?.query?.tab === TabType.ETAXI}
    hasFooter={_props?.query?.from !== 'hoiango.com'}
    hideHeader={_props?.query?.from === 'hoiango.com'}
    showTabs
    hasPadding={false} //{_props?.query?.tab === TabType.ETAXI}
    backgroundColor="#F1F8FF"
    headerBackgroundColor={
      !_props?.query?.tab
        ? 'rgba(1, 47, 91, 0.01)'
        : undefined
    }
  >
    {page}
    {/*{
      _props?.query?.from !== 'hoiango.com' ? (
        <>
          <WhatsAppChat />
          <Hepler />
        </>
      ) : null
    }*/}

  </Layout>
);

Home.type = 'public';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, query } = context;
  const tabType = typeof query?.tab === 'string' ? query.tab : undefined;
  const isTourTab = tabType === TabType.TOUR;
  let tours: any[] = [];
  let homeContent: any = {};
  const API_URL = process.env.NEXT_PUBLIC_SERVER_SIDE_BASE_URL ?? process.env.NEXT_PUBLIC_BASE_URL;
  const lang = locale ?? 'en';
  const i18nProps = await serverSideTranslations(lang, [
    'validate',
    'common',
    'aboutUs',
    'error',
  ]);
  const normalizedLang = lang.split('-')[0];
  const i18nStore = (i18nProps as any)?._nextI18Next?.initialI18nStore;
  const localizedFallbackTitle =
    i18nStore?.[lang]?.common?.home?.metaTitle ??
    i18nStore?.[normalizedLang]?.common?.home?.metaTitle;
  const localizedFallbackDescription =
    i18nStore?.[lang]?.common?.home?.metaDescription ??
    i18nStore?.[normalizedLang]?.common?.home?.metaDescription;
  const fallbackTitle =
    typeof localizedFallbackTitle === 'string' && localizedFallbackTitle.trim()
      ? localizedFallbackTitle
      : 'HoiAnGO E-Taxi, Sightseeing & Tours';
  const fallbackDescription =
    typeof localizedFallbackDescription === 'string' && localizedFallbackDescription.trim()
      ? localizedFallbackDescription
      : 'Hoi An GO E-taxi Sightseeing Tours in Hoi An. Getting around Hoi An has never been easier with easy online booking with Hoi An GO';
  let metadata: PageMetaOutput = {
    title: fallbackTitle,
    description: fallbackDescription,
  };

  try {
    metadata = await getMetaDataPage({
      type: 'home_content',
      fallbackTitle,
      fallbackDescription,
    });
  } catch (error) {
    console.error('[home] failed to load metadata', error);
  }

  if (isTourTab) {
    try {
      const list_tour_url = `${API_URL}booking_tour/${locale}/list_tour`;
      const res = await fetch(list_tour_url);

      if (!res.ok) {
        throw new Error(`Failed to fetch, status code: ${res.status}`);
      }

      const data = await res.json();
      tours = convertImages(data);
    } catch (error) {
      console.error('Error fetching tours:', error);
    }
  } else {
    try {
      homeContent = await getHomeContent({ lang });
    } catch (error) {
      console.error('Error fetching home content:', error);
    }
    try {
      const [experienceTours, adventureTours, etaxi, travelArticles] = await Promise.allSettled([
        Promise.resolve(tourService.fetchTours({
          lang,
          type: TourType.EXPERIENCE,
        })),
        Promise.resolve(tourService.fetchTours({
          lang,
          type: TourType.ADVENTURES,
        })),
        Promise.resolve(getEtaxi({ lang })),
        Promise.resolve(getArticles({ lang, limit: 3 })),
      ]);
      homeContent.etaxi = etaxi.status === 'fulfilled' ? etaxi.value : null;
      homeContent.travel_articles = travelArticles.status === 'fulfilled' ? travelArticles.value.map(x => {
        delete x.content;
        return x;
      }) : [];
      homeContent.experience_tours =
        experienceTours.status === 'fulfilled'
          ? experienceTours.value.slice(0, 4)
          : [];
      homeContent.adventure_tours =
        adventureTours.status === 'fulfilled'
          ? adventureTours.value.slice(0, 4)
          : [];

      if (!isTourTab) {
        tours = [
          ...homeContent.adventure_tours,
          ...homeContent.experience_tours,
        ];
      }
    } catch (error) {
      console.error('Error fetching home content:', error);
    }
  }
  return {
    props: {
      ...i18nProps,
      // Will be passed to the page component as props
      tours,
      homeContent,
      metadata,
      locale,
      query,
    },
  };
}

export default Home;
